import React, { useEffect, useState, useCallback } from 'react';
import { CookingPot, Clock, Sun, Moon, Coffee, Hamburger } from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';
import { CookPhotoModal } from '@/components/meals/CookPhotoModal';

const API_BASE = apiOrigin();

const splitDishes = (v) => {
  const s = typeof v === 'string' ? v : (v && typeof v === 'object' ? (v.default || '') : '');
  return s.split(/[,/]/).map(x => x.trim()).filter(Boolean);
};

const MEAL_CONFIG = {
  breakfast: { hindi: 'नाश्ता', icon: Coffee, color: '#F59E0B', bg: 'rgba(245,158,11,0.08)' },
  lunch: { hindi: 'दोपहर का खाना', icon: Sun, color: '#FF6B35', bg: 'rgba(255,107,53,0.08)' },
  snacks: { hindi: 'स्नैक्स', icon: Hamburger, color: '#8B5CF6', bg: 'rgba(139,92,246,0.08)' },
  dinner: { hindi: 'रात का खाना', icon: Moon, color: '#3B82F6', bg: 'rgba(59,130,246,0.08)' },
};

export default function CookViewPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [expandedMeal, setExpandedMeal] = useState(null);
  const [photoDish, setPhotoDish] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/meals/cook-view`);
      const json = await res.json();
      setData(json);
      // Auto-expand current meal
      if (json.current_meal && !expandedMeal) {
        setExpandedMeal(json.current_meal);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 10 minutes
  useEffect(() => {
    const interval = setInterval(fetchData, 600000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Clock
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="text-center">
          <CookingPot size={48} className="text-[#FF6B35] mx-auto mb-4 animate-bounce" />
          <p className="text-xl text-neutral-400">लोड हो रहा है...</p>
        </div>
      </div>
    );
  }

  const plan = data?.plan;
  const hindiMenu = data?.hindi_menu;
  const hindiMeals = hindiMenu?.meals || [];

  const timeStr = currentTime.toLocaleTimeString('hi-IN', { hour: '2-digit', minute: '2-digit' });
  const dateStr = currentTime.toLocaleDateString('hi-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="cook-view-page">
      {/* Header */}
      <div className="px-6 sm:px-8 py-5 border-b border-white/5" style={{ background: 'rgba(255,107,53,0.03)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-[#FF6B35]/10 flex items-center justify-center">
              <CookingPot size={28} weight="fill" className="text-[#FF6B35]" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">आज का मेनू</h1>
              <p className="text-sm text-neutral-400 mt-0.5">{dateStr}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl sm:text-4xl font-bold font-mono text-[#FF6B35]" data-testid="cook-clock">{timeStr}</p>
            <p className="text-xs text-neutral-500 uppercase mt-0.5">{data?.diet_type || 'Jain Vegetarian'}</p>
          </div>
        </div>
      </div>

      {/* No Plan */}
      {!plan && (
        <div className="px-6 py-20 text-center">
          <CookingPot size={64} className="text-neutral-700 mx-auto mb-4" />
          <p className="text-2xl text-neutral-500">आज का मेनू अभी तय नहीं हुआ</p>
          <p className="text-lg text-neutral-600 mt-2">No meal plan for today</p>
        </div>
      )}

      {/* Meal Cards */}
      {plan && (
        <div className="px-4 sm:px-8 py-6 space-y-4">
          {['breakfast', 'lunch', 'snacks', 'dinner'].map(mealKey => {
            if (!plan[mealKey]) return null;
            const config = MEAL_CONFIG[mealKey];
            const Icon = config.icon;
            const isActive = data?.current_meal === mealKey;
            const isExpanded = expandedMeal === mealKey;
            const hindiData = hindiMeals.find(m =>
              m.meal_type === (mealKey === 'breakfast' ? 'nashta' : mealKey === 'lunch' ? 'dopahar' : mealKey === 'dinner' ? 'raat' : 'snacks')
            );

            return (
              <div key={mealKey}
                data-testid={`cook-meal-${mealKey}`}
                onClick={() => setExpandedMeal(isExpanded ? null : mealKey)}
                className="rounded-3xl overflow-hidden cursor-pointer transition-all"
                style={{
                  background: isActive ? config.bg : 'rgba(255,255,255,0.02)',
                  border: `2px solid ${isActive ? config.color + '40' : 'rgba(255,255,255,0.06)'}`,
                }}>
                {/* Meal Header */}
                <div className="px-5 sm:px-6 py-4 sm:py-5 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ background: config.color + '15' }}>
                      <Icon size={32} weight="fill" style={{ color: config.color }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-xl sm:text-2xl font-bold" style={{ color: isActive ? config.color : 'white' }}>
                          {config.hindi}
                        </h2>
                        {isActive && (
                          <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase animate-pulse"
                            style={{ background: config.color + '20', color: config.color }}>
                            अभी
                          </span>
                        )}
                      </div>
                      <p className="text-base sm:text-lg text-neutral-300 mt-1">
                        {hindiData?.dish_name_hindi || plan[mealKey]}
                      </p>
                      {hindiData?.dish_name_hindi && (
                        <p className="text-sm text-neutral-500">{plan[mealKey]}</p>
                      )}
                      {/* Tap-for-photo dish chips */}
                      <div className="flex flex-wrap gap-2 mt-2.5">
                        {splitDishes(plan[mealKey]).map((dishName) => {
                          const m = (data?.dish_media || {})[dishName] || {};
                          return (
                            <button key={dishName}
                              onClick={(e) => { e.stopPropagation(); setPhotoDish({ name: dishName, ...m }); }}
                              data-testid={`cook-dish-chip-${(m.dish_key || dishName).toString().replace(/\s+/g, '_')}`}
                              className="flex items-center gap-2 rounded-2xl pr-3 pl-1.5 py-1.5 transition-transform active:scale-95"
                              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}>
                              {m.image_url ? (
                                <img src={`${API_BASE}${m.image_url}`} alt={dishName}
                                  className="w-11 h-11 rounded-xl object-cover" loading="lazy" />
                              ) : (
                                <span className="w-11 h-11 rounded-xl bg-white/5 flex items-center justify-center">
                                  <CookingPot size={22} className="text-neutral-500" />
                                </span>
                              )}
                              <span className="text-sm sm:text-base text-neutral-200 font-medium">{dishName}</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    {hindiData?.time_minutes && (
                      <span className="flex items-center gap-1 text-sm text-neutral-500">
                        <Clock size={16} /> {hindiData.time_minutes} min
                      </span>
                    )}
                    <span className="text-neutral-500 text-lg">{isExpanded ? '▲' : '▼'}</span>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && hindiData && (
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6 border-t border-white/5">
                    {/* Ingredients */}
                    {hindiData.ingredients && hindiData.ingredients.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-bold text-neutral-400 uppercase tracking-wider mb-3">सामग्री (Ingredients)</p>
                        <div className="flex flex-wrap gap-2">
                          {hindiData.ingredients.map((ing, i) => (
                            <span key={i} className="px-3 py-2 rounded-xl text-sm sm:text-base font-medium"
                              style={{ background: config.color + '10', color: config.color, border: `1px solid ${config.color}20` }}>
                              {ing}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Steps */}
                    {hindiData.steps && hindiData.steps.length > 0 && (
                      <div className="mt-5">
                        <p className="text-sm font-bold text-neutral-400 uppercase tracking-wider mb-3">बनाने की विधि (Steps)</p>
                        <div className="space-y-3">
                          {hindiData.steps.map((step, i) => (
                            <div key={i} className="flex items-start gap-3">
                              <span className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold"
                                style={{ background: config.color + '15', color: config.color }}>
                                {i + 1}
                              </span>
                              <p className="text-base sm:text-lg text-neutral-200 leading-relaxed pt-1">{step}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Tips */}
                    {hindiData.tips && (
                      <div className="mt-4 px-4 py-3 rounded-xl" style={{ background: config.color + '08', border: `1px solid ${config.color}15` }}>
                        <p className="text-sm" style={{ color: config.color }}>💡 {hindiData.tips}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Expanded but no Hindi data — show dish name in large font */}
                {isExpanded && !hindiData && (
                  <div className="px-5 sm:px-6 pb-5 border-t border-white/5">
                    <p className="text-lg text-neutral-400 mt-4">
                      विस्तृत विधि उपलब्ध नहीं है। कृपया ऊपर दिए गए व्यंजन के अनुसार बनाएं।
                    </p>
                    <p className="text-sm text-neutral-600 mt-1">Detailed recipe not available. Please prepare as per the dish name above.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Jain calendar (day-specific food discipline) */}
      {(data?.jain_today?.no_veg || data?.jain_today?.chaturmas) && (
        <div className="px-4 sm:px-8 pb-4" data-testid="cook-jain-banner">
          <div className="rounded-3xl p-5 text-center" style={{ background: 'rgba(245,158,11,0.08)', border: '2px solid rgba(245,158,11,0.4)' }}>
            {data.jain_today.paryushan ? (
              <>
                <p className="text-xl sm:text-2xl font-bold text-rose-300">🙏 पर्युषण पर्व चल रहा है</p>
                <p className="text-base text-rose-200/90 mt-1">बिलकुल कोई सब्ज़ी नहीं — फरसाण, दाल-बेसन का खाना और बाजरी/जुवार/रागी की रोटी (खमण, खांडवी, गट्टे, दाल वड़ा, कढ़ी)</p>
              </>
            ) : data.jain_today.no_veg ? (
              <>
                <p className="text-xl sm:text-2xl font-bold text-amber-300">
                  🙏 आज {{ chaudas: 'चौदस', atham: 'आठम', pancham: 'पंचम' }[data.jain_today.label] || 'तिथि'} है
                </p>
                <p className="text-base text-amber-200/90 mt-1">आज सब्ज़ी नहीं बनेगी — सिर्फ़ दाल, बेसन, दही/कढ़ी और अनाज का खाना (गट्टे, दाल वड़ा, खांडवी, खिचड़ी, पापड़-मेथी)</p>
              </>
            ) : (
              <>
                <p className="text-xl sm:text-2xl font-bold text-emerald-300">🙏 चातुर्मास चल रहा है</p>
                <p className="text-base text-emerald-200/90 mt-1">जड़ वाली सब्ज़ी बिलकुल नहीं — आलू, प्याज़, लहसुन, गाजर, मूली, अदरक बंद</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* Kids Dabba */}
      {(data?.dabba_today || []).length > 0 && (
        <div className="px-4 sm:px-8 pb-6 space-y-3" data-testid="cook-dabba-section">
          <h2 className="text-lg sm:text-xl font-bold text-yellow-300 px-1">🍱 बच्चों का डब्बा (Kids Tiffin)</h2>
          {data.dabba_today.map((d, i) => (
            <div key={i} className="rounded-3xl p-5" data-testid={`cook-dabba-${i}`}
              style={{ background: 'rgba(234,179,8,0.06)', border: '2px solid rgba(234,179,8,0.25)' }}>
              <p className="text-lg font-bold text-white mb-3">{d.kid_name}</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[['school_tiffin', '🍛', 'स्कूल टिफ़िन'], ['school_snack', '🍎', 'स्कूल स्नैक'], ['bus_snack', '🚌', 'बस स्नैक']].map(([key, emoji, label]) => d[key] && (
                  <div key={key} className="rounded-2xl px-4 py-3" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <p className="text-xs text-yellow-500/80 font-bold mb-1">{emoji} {label}</p>
                    <p className="text-base text-neutral-200">{d[key].replace(/\s*\(new\)\s*$/i, '')}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Raw Hindi response fallback */}
      {hindiMenu?.raw && (
        <div className="px-6 sm:px-8 py-6">
          <div className="rounded-3xl p-6" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
            <pre className="text-lg text-neutral-300 whitespace-pre-wrap leading-relaxed font-sans">{hindiMenu.raw}</pre>
          </div>
        </div>
      )}

      {/* Full-screen dish photo + step pictures */}
      {photoDish && (
        <CookPhotoModal dish={photoDish} apiBase={API_BASE} onClose={() => setPhotoDish(null)} />
      )}

      {/* Footer */}
      <div className="px-6 sm:px-8 py-4 border-t border-white/5 text-center">
        <p className="text-xs text-neutral-600">HomeCommand Kitchen Display — Auto-refreshes every 10 minutes</p>
      </div>
    </div>
  );
}
